<template>
  <DialogHeader>Copyright owners</DialogHeader>
  <DialogContent>
    <div class="mb-[22px]">
      Select from this list of copyright owners and organizations to attribute
      ownership when you add your work to Dacr.
    </div>
    <Table.Base
      ref="table"
      class="max-h-[238px] overflow-y-auto mx-[-16px] mb-[18px] tablet-h:mx-[-30px]"
      :auto-columns="false"
    >
      <Table.Row
        v-for="owner in copyrightOwners"
        :key="owner.id"
        class="hover:bg-transparent cursor-default! first:border-t border-gray-800/8"
      >
        <Table.Cell class="h-[64px]! pl-4 tablet-h:pl-[30px]">
          <Avatar :src="owner.avatarUrl" :size="32" class="mr-2.5" />
          <div>
            <div>{{ owner.name }}</div>
            <div
              v-if="isOrganization(owner)"
              class="text-[13px] leading-[18px] text-gray-800/50"
            >
              Managed by {{ owner.owner.name }}
            </div>
          </div>
        </Table.Cell>
        <Table.Cell class="h-[64px]! pr-4 tablet-h:pr-[22px]">
          <Pill v-if="isAccount(owner)">You</Pill>
          <Float.Base v-else>
            <Float.Trigger>
              <div
                class="w-[30px] h-[30px] rounded-sm hover:bg-gray-800/6 flex items-center justify-center cursor-pointer"
              >
                <Icon
                  name="ThreeDots"
                  :size="18"
                  class="stroke-gray-800 fill-gray-800"
                />
              </div>
            </Float.Trigger>
            <Float.Component>
              <MenuList>
                <MenuListItem
                  class="stroke-red-700!"
                  @click="handleUpdateClick(owner)"
                >
                  <Icon name="Pencil" :size="18" class="stroke-gray-800 mr-3" />
                  <div>Edit {{ getType(owner) }}</div>
                </MenuListItem>

                <MenuListSeparator />

                <MenuListItem
                  class="stroke-red-700!"
                  @click="handleDeleteClick(owner)"
                >
                  <Icon name="Delete" :size="18" class="stroke-red-700 mr-3" />
                  <div class="text-red-700">Remove</div>
                </MenuListItem>
              </MenuList>
            </Float.Component>
          </Float.Base>
        </Table.Cell>
      </Table.Row>
    </Table.Base>
    <div
      class="flex items-center cursor-pointer w-fit"
      @click="handleNewOwnerClick"
    >
      <Icon :size="24" class="stroke-green-900 mr-[15px]" name="Plus" />
      <div class="text-green-800 font-medium">Add new copyright owner</div>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import {
  CopyrightOwnerType,
  useCopyrightOwnersNewQuery,
  type CopyrightOwnerNew,
} from '@/shared/graphql/legacy/generated'
import { Pill, Table, Avatar, Float, Icon } from '@dacr/ui'
import {
  MenuList,
  MenuListItem,
  MenuListSeparator,
} from '@/shared/components/Menu'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const { close, open } = useDialog()

const { result, fetchMore, loading } = useCopyrightOwnersNewQuery()

const table = ref<InstanceType<typeof Table.Base>>()

const copyrightOwners = computed(() => result.value?.copyrightOwnersNew.items)

const getType = (owner: CopyrightOwnerNew) => {
  if (owner.type === CopyrightOwnerType.Personal) return 'owner'
  return 'organization'
}

const isAccount = (owner: CopyrightOwnerNew) =>
  owner.type === CopyrightOwnerType.Account

const isOrganization = (owner: CopyrightOwnerNew) =>
  owner.type === CopyrightOwnerType.Organization

const handleNewOwnerClick = () => {
  open(Dialogs.NewCopyrightOwner, { stack: true })
}

const handleUpdateClick = (owner: CopyrightOwnerNew) => {
  document.body.click()

  open(Dialogs.EditCopyrightOwner, {
    props: { owner },
    stack: true,
  })
}

const handleDeleteClick = (owner: CopyrightOwnerNew) => {
  document.body.click()

  open(Dialogs.DeleteCopyrightOwner, {
    props: { owner },
  })
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight

  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPage()
  }
}

const fetchNextPage = () => {
  if (result.value?.copyrightOwnersNew.next && !loading.value) {
    fetchMore({
      variables: {
        next: result.value.copyrightOwnersNew.next,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          ...newData,
          copyrightOwnersNew: {
            ...newData.copyrightOwnersNew,
            items: [
              ...previousData.copyrightOwnersNew.items,
              ...newData.copyrightOwnersNew.items,
            ],
          },
        }
      },
    })
  }
}

onMounted(() => {
  table.value?.$el.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  table.value?.$el.removeEventListener('scroll', handleScroll)
})
</script>
